import React, { createContext, useContext, useState, useCallback } from 'react';
import AchievementPopup from '@/components/AchievementPopup';

const AchievementContext = createContext(null);

export function AchievementProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const [currentAchievement, setCurrentAchievement] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [language, setLanguage] = useState('de');

  const showAchievement = useCallback((achievement, lang = 'de') => {
    setLanguage(lang);
    setQueue(prev => [...prev, achievement]);
  }, []);

  const showNextAchievement = useCallback(() => {
    if (queue.length > 0) {
      const [next, ...rest] = queue;
      setCurrentAchievement(next);
      setQueue(rest);
      setIsOpen(true);
    }
  }, [queue]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setCurrentAchievement(null);
    // Show next achievement if there are more in queue
    setTimeout(() => {
      if (queue.length > 0) {
        showNextAchievement();
      }
    }, 300);
  }, [queue, showNextAchievement]);

  // Show achievement when one is added to queue
  React.useEffect(() => {
    if (queue.length > 0 && !currentAchievement && !isOpen) {
      showNextAchievement();
    }
  }, [queue, currentAchievement, isOpen, showNextAchievement]);

  return (
    <AchievementContext.Provider value={{ showAchievement }}>
      {children}
      <AchievementPopup
        achievement={currentAchievement}
        isOpen={isOpen}
        onClose={handleClose}
        language={language}
      />
    </AchievementContext.Provider>
  );
}

export function useAchievementPopup() {
  const context = useContext(AchievementContext);
  if (!context) {
    throw new Error('useAchievementPopup must be used within an AchievementProvider');
  }
  return context;
}
